import * as React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'

import { Table } from '../table/Table'
import { TableBody } from '../table/TableBody'
import { AsyncDataState } from './DataTable.types'
import { DataTableBody } from './DataTableBody'
import { useDataTable } from './DataTableContext'
import { DataTableHead } from './DataTableHead'
import { DataTableLoading } from './DataTableLoading'

const TableContainer = styled('div', {
  base: ['p-2', 'bg-white', 'rounded-lg']
})

export type DataTableTableProps = Omit<
  React.ComponentProps<typeof Table>,
  'children' | 'numberOfStickyColumns'
> &
  Partial<
    Pick<React.ComponentProps<typeof DataTableHead>, 'theme' | 'sortable'>
  > &
  Partial<Pick<React.ComponentProps<typeof TableBody>, 'striped'>> & {
    scrollOptions?: {
      hasStickyHeader?: boolean
      stickyHeaderOffset?: number
      headerCss?: string
      numberOfStickyColumns?: number
      scrollContainerCls?: string | string[]
      showTotalInFirstColumn?: boolean
      totalSuffix?: string
    }
    rowAction?: (row: Record<string, unknown>, event: React.MouseEvent) => void
    withContainer?: boolean
  }

export const DataTableTable = ({
  sortable,
  striped,
  theme = 'white',
  className,
  scrollOptions = {
    numberOfStickyColumns: 0,
    stickyHeaderOffset: 0,
    hasStickyHeader: false,
    showTotalInFirstColumn: true,
    totalSuffix: 'items'
  },
  rowAction,
  withContainer = false,
  ...props
}: DataTableTableProps): React.JSX.Element | null => {
  const {
    asyncDataState,
    getTotalRows,
    getCanSomeRowsExpand,
    rowSelection,
    enableRowSelection,
    tableId,
    setTablePosition
  } = useDataTable()

  const tableRef = React.useRef<HTMLTableElement | null>(null)
  const isTableVisible = React.useRef(false)
  const isScrolling = React.useRef<boolean>(false)

  const scrollTarget = React.useMemo(() => {
    if (typeof window === 'undefined') return null
    return window
  }, [])

  React.useEffect(() => {
    if (!scrollTarget || typeof IntersectionObserver === 'undefined') {
      return
    }

    const updatePosition = () => {
      if (tableRef.current) {
        const tableTop = tableRef.current.getBoundingClientRect().top
        const tableBottom = tableRef.current.getBoundingClientRect().bottom

        setTablePosition((prevPosition) => ({
          ...prevPosition,
          top: tableTop,
          bottom: tableBottom
        }))
      }
      isScrolling.current = false
    }

    const handleScroll = () => {
      if (!isScrolling.current) {
        requestAnimationFrame(updatePosition)
        isScrolling.current = true
      }
    }

    if (!tableRef.current) return

    const observerOffset = scrollOptions?.stickyHeaderOffset || 0

    const observerOptions = {
      rootMargin: `-${observerOffset}px 0px -${observerOffset}px 0px`,
      threshold: 0
    }

    const handleTableIntersection = (entries: IntersectionObserverEntry[]) => {
      if (!entries || entries.length === 0) return
      const entry = entries[0]

      isTableVisible.current = entry.isIntersecting

      if (scrollTarget) {
        if (entry.isIntersecting) {
          // add scroll listener
          scrollTarget?.addEventListener('scroll', handleScroll)
        } else {
          // remove scroll listener
          scrollTarget?.removeEventListener('scroll', handleScroll)
        }
      }

      setTablePosition((prevPosition) => ({
        ...prevPosition,
        isVisible: entry.isIntersecting
      }))
    }

    const tableObserver = new IntersectionObserver(
      handleTableIntersection,
      observerOptions
    )

    //only track table position if we have a sticky header or bulk actions active
    if (
      tableRef.current &&
      (scrollOptions.hasStickyHeader ||
        Object.keys(rowSelection || {}).length > 0)
    ) {
      tableObserver.observe(tableRef.current)
    }

    return () => {
      tableObserver.disconnect()
      if (scrollTarget) {
        scrollTarget?.removeEventListener('scroll', handleScroll)
      }
    }
  }, [
    rowSelection,
    scrollOptions.hasStickyHeader,
    scrollOptions?.stickyHeaderOffset,
    scrollTarget,
    setTablePosition
  ])

  const isPending = asyncDataState === AsyncDataState.PENDING
  const isEmpty = !isPending && getTotalRows() === 0

  const buildScrollContainerKey = (): string => {
    let controlColumnCount = 0

    if (getCanSomeRowsExpand() && !!enableRowSelection) {
      controlColumnCount = 2
    } else if (getCanSomeRowsExpand() || !!enableRowSelection) {
      controlColumnCount = 1
    }

    return `${tableId}_${controlColumnCount}_control_columns`
  }

  if (isEmpty) return null

  const tableComponent = (
    <Table
      corners="round"
      size="lg"
      {...props}
      ref={tableRef}
      numberOfStickyColumns={scrollOptions.numberOfStickyColumns}
      scrollContainerCls={scrollOptions.scrollContainerCls}
      scrollContainerkey={buildScrollContainerKey()}
      className={cn(
        isPending && [
          'opacity-50',
          'pointer-events-none',
          'transition-opacity',
          'duration-250',
          'linear',
          'delay-150'
        ],
        className
      )}
    >
      <DataTableHead
        theme={theme}
        sortable={sortable}
        isSticky={scrollOptions.hasStickyHeader}
        stickyOffset={scrollOptions.stickyHeaderOffset}
        className={scrollOptions.headerCss}
        showTotalInFirstColumn={scrollOptions.showTotalInFirstColumn}
        totalSuffix={scrollOptions.totalSuffix}
      />
      <DataTableBody striped={striped} rowAction={rowAction} />
    </Table>
  )

  return (
    <>
      <DataTableLoading />
      {withContainer ? (
        <TableContainer
          className={cn(
            scrollOptions.numberOfStickyColumns === 0 && 'overflow-x-auto'
          )}
        >
          {tableComponent}
        </TableContainer>
      ) : (
        tableComponent
      )}
    </>
  )
}
